<template>
  <f7-sheet @sheet:open="onOpen">
    <f7-toolbar :color-theme="themeColor">
      <f7-link>時間選擇</f7-link>
      <f7-link @click="$f7.sheet.close()">確定</f7-link>
    </f7-toolbar>
    <div id="picker-date-container" />
  </f7-sheet>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import * as R from 'ramda'

export default {
  dependencies: ['themeColor'],
  data () {
    return {
      timePicker: null
    }
  },
  computed: {
    ...mapState('menuModule', ['businessHours']),
    weekdayIndex () { return this.timeIndices.split('-')[0] },
    businessHourIndex () { return this.timeIndices.split('-')[1] },
    boundary () { return this.timeIndices.split('-')[2] },
    menuBusinessHours () { return this.businessHours[this.menuVariationProp][this.weekdayIndex] },
    businessHourInterval () { return this.menuBusinessHours[this.businessHourIndex] },
    hour () { return this.businessHourInterval[this.boundary === 'start' ? 0 : 1].split(':')[0] },
    minute () { return this.businessHourInterval[this.boundary === 'start' ? 0 : 1].split(':')[1] },
    availableTime () {
      const businessHoursWithoutCurrent = R.pipe(
        R.filter(x => x[0] !== x[1]),
        R.differenceWith(
          (x, y) => x[0] === y[0] && x[1] === y[1],
          R.__,
          [this.businessHourInterval]
        ),
        R.sortBy(R.nth(0))
      )(this.menuBusinessHours)

      return R.pipe(
        R.flatten,
        R.prepend('00:00'),
        R.append('23:59'),
        R.aperture(2),
        R.difference(R.__, businessHoursWithoutCurrent)
      )(businessHoursWithoutCurrent)
    },
    availableOptions () {
      return R.pipe(
        R.map(p => {
          const start = moment(p[0], 'HH:mm')
          const end = moment(p[1], 'HH:mm')
          const options = []

          let t = start
          for (; t <= end; t = t.add(5, 'm')) {
            options.push([t.format('HH'), t.format('mm')])
          }
          if (t.format('HH:mm' === '00:00')) {
            t.subtract(1, 'm')
            options.push([t.format('HH'), t.format('mm')])
          }

          return options
        }),
        R.unnest,
        R.groupBy(p => p[0]),
        R.map(l => l.map(p => p[1]))
      )(this.availableTime)
    },
    availableMomentInterval () {
      return this.availableTime.map(p => p.map(v => moment(v, 'HH:mm')))
    },
    hourList () {
      return R.pipe(
        R.keys,
        R.sortBy(Number)
      )(this.availableOptions)
    }
  },
  props: {
    menuVariationProp: String,
    timeIndices: String
  },
  methods: {
    ...mapActions('menuModule', ['setBusinessHour']),
    onOpen () {
      this.timePicker.resizeCols()
    },
    onStartChange (value) {
      const startMoment = moment(value, 'HH:mm')
      const endMoment = moment(this.businessHourInterval[1], 'HH:mm')
      const endValue = this.availableMomentInterval.reduce((endValue, [startBound, endBound]) =>
        endValue || ((startMoment.isSameOrAfter(startBound) &&
          startMoment.isSameOrBefore(endBound) &&
          (endMoment.isBefore(startMoment) || endMoment.isAfter(endBound)))
          ? endBound.format('HH:mm')
          : null), null
      ) || this.businessHourInterval[1]

      this.setBusinessHour({
        menuVariation: this.menuVariationProp,
        weekdayIndex: this.weekdayIndex,
        index: this.businessHourIndex,
        interval: [value, endValue]
      })
    },
    onEndChange (value) {
      const startMoment = moment(this.businessHourInterval[0], 'HH:mm')
      const endMoment = moment(value, 'HH:mm')

      const startValue = this.availableMomentInterval.reduce((startValue, [startBound, endBound]) =>
        startValue || ((endMoment.isSameOrAfter(startBound) &&
          endMoment.isSameOrBefore(endBound) &&
          (endMoment.isBefore(startMoment) || startMoment.isBefore(startBound)))
          ? startBound.format('HH:mm')
          : null), null
      ) || this.businessHourInterval[0]

      this.setBusinessHour({
        menuVariation: this.menuVariationProp,
        weekdayIndex: this.weekdayIndex,
        index: this.businessHourIndex,
        interval: [startValue, value]
      })
    }
  },
  mounted () {
    this.timePicker = this.$f7.picker.create({
      containerEl: '#picker-date-container',
      inputEl: '#',
      toolbar: false,
      rotateEffect: false,
      cols: [{
        textAlign: 'center',
        values: this.hourList,
        onChange: (picker, value) => {
          if (picker.cols[2].replaceValues) {
            picker.cols[2].replaceValues(this.availableOptions[value])
          }
        }
      }, {
        divider: true,
        content: ':'
      }, {
        textAlign: 'center',
        values: this.availableOptions[this.hour]
      }],
      on: {
        change: (picker, values, displayValues) => {
          if (picker.opened) {
            if (this.boundary === 'start') {
              this.onStartChange(`${values[0]}:${values[1]}`)
            } else {
              this.onEndChange(`${values[0]}:${values[1]}`)
            }
          }
        },
        open: picker => { picker.setValue([this.hour, this.minute]) }
      },
      value: [this.hour, this.minute]
    })
  }
}
</script>
